import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuthContext } from "../../hooks/useAuthContext";
import { ApiConnectionString } from "../../constants/ApiConntection";
import { Link, useNavigate } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [resetRequested, setResetRequested] = useState(false);

  const { dispatch, user } = useAuthContext();
  const navigate = useNavigate();

  const mainSiteUrl = "https://neurotherapydirectory.com/";

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    setError("");

    const loginDetails = { email, password };

    axios
      .post(`${ApiConnectionString}/api/login/`, loginDetails)
      .then((res) => {
        if (res.status === 200) {
          dispatch({ type: "LOGIN", payload: res.data });
          navigate("/");
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          setError(err.response.data[0]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email");
      return;
    }

    if (resetRequested) return;

    setForgotLoading(true);
    setError("");
    setForgotMessage("");

    axios
      .post(
        `${ApiConnectionString}/api/ForgotPassword?email=${encodeURIComponent(
          email
        )}`
      )
      .then(() => {
        setForgotMessage("If that email exists we have sent reset instructions");
        setResetRequested(true);
      })
      .catch((err) => {
        if (err.response && err.response.data) {
          setError(err.response.data[0] || "Something went wrong");
        } else {
          setError("Something went wrong");
        }
      })
      .finally(() => {
        setForgotLoading(false);
      });
  };

  if (loading) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center py-4">
      <div className="w-100" style={{ maxWidth: "420px", padding: "0 18px" }}>
        <div className="shadow-lg bcs-modal-content">
          <div className="bcs-modal-header">
            <div className="text-center w-100 mt-1">
              <div className="bcs-title brand-dark-purple">
                {isForgotPassword ? "Reset your password" : "Therapist login"}
              </div>

              <div className="bcs-subtitle mt-1">
                {isForgotPassword
                  ? "Enter your email to get reset instructions"
                  : "This login is for therapists on Neuro Counselling"}
              </div>

              {!isForgotPassword && (
                <div className="mt-2">
                  <a
                    href={mainSiteUrl}
                    className="btn btn-link p-0 text-decoration-none bcs-link"
                  >
                    Go to the main website
                  </a>
                </div>
              )}
            </div>

            <div className="w-100 bcs-divider"></div>
          </div>

          <div className="bcs-modal-body">
            {!isForgotPassword && (
              <form onSubmit={handleLogin}>
                <div className="mb-3 text-start">
                  <label className="form-label brand-purple">Email</label>
                  <input
                    type="email"
                    className="form-control bcs-brand-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3 text-start">
                  <label className="form-label brand-purple">Password</label>

                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control bcs-brand-input"
                      placeholder="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />

                    <button
                      type="button"
                      className="btn brand-purple-outline bcs-input-addon-btn"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger py-2 mb-3">{error}</div>
                )}

                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input bcs-brand-check"
                      type="checkbox"
                      id="rememberMe"
                    />

                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember me
                    </label>
                  </div>

                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none bcs-link"
                    onClick={() => {
                      setError("");
                      setForgotMessage("");
                      setResetRequested(false);
                      setIsForgotPassword(true);
                    }}
                  >
                    Forgot password
                  </button>
                </div>

                <button
                  type="submit"
                  className="btn brand-purple-inline w-100 bcs-primary-btn"
                  disabled={loading}
                >
                  {loading ? "Logging in..." : "Login"}
                </button>
              </form>
            )}

            {isForgotPassword && (
              <form onSubmit={handleForgotPassword}>
                <div className="mb-3 text-start">
                  <label className="form-label brand-purple">Email</label>

                  <input
                    type="email"
                    className="form-control bcs-brand-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setResetRequested(false);
                    }}
                    required
                  />
                </div>

                {error && (
                  <div className="alert alert-danger py-2 mb-3">{error}</div>
                )}

                {forgotMessage && (
                  <div className="alert alert-success py-2 mb-3">
                    {forgotMessage}
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center mb-4">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none bcs-link"
                    onClick={() => {
                      setError("");
                      setForgotMessage("");
                      setIsForgotPassword(false);
                    }}
                  >
                    Back to login
                  </button>
                </div>

                <button
                  type="submit"
                  className="btn brand-purple-inline w-100 bcs-primary-btn"
                  disabled={forgotLoading || resetRequested}
                >
                  {forgotLoading
                    ? "Sending..."
                    : resetRequested
                    ? "Reset link sent"
                    : "Send reset link"}
                </button>
              </form>
            )}
          </div>

          {!isForgotPassword && (
            <div className="bcs-modal-footer d-flex justify-content-center align-items-center gap-2">
              <span className="text-muted">Do not have an account</span>

              <Link to={"/SignUp"} className="text-decoration-none bcs-link">
                Sign up
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
